import { KatButton, KatDropdownButton } from "@amzn/katal-react";
import React, { type FC, useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  type InboundDocument,
  InboundStatus,
} from "src/api/interfaces/inbound_document";
import { Operation } from "src/api/interfaces/permissions";
import withPermissionsOrDefault from "src/components/AICLayout/AICHeader/Permissions/WithPermissions";
import { useAlertContext } from "src/components/Alert/AlertContext";
import { alertHandler } from "src/components/Alert/handlers";
import type { DataTableCellChildComponentProps } from "src/components/DataTable/DataTableDefinition";
import handleGetDownloadDocumentUrl from "src/components/Pages/DocumentInbound/Tabs/Documents/DownloadDocumentButton/Handlers/get_download_document_url_handler";
import { capitalizeText } from "src/utils";
import { INITIAL_STATE, LOADING_STATE } from "./states";

const DOWNLOAD_BUTTON_STRING_TEMPLATE =
  "aic_doc_page_documents_tab_field_button_download";

const shouldShowButton = (inboundContent: InboundDocument) => {
  return inboundContent.status !== InboundStatus.NOT_INGESTED;
};

const DownloadDocumentButton: FC<DownloadDocumentButtonProps> = ({
  content,
}) => {
  const { t } = useTranslation();
  const [buttonState, setButtonState] = useState(INITIAL_STATE);
  const { setAlerts, alerts } = useAlertContext();

  const handleClick = useCallback(
    (attachment?: string) => {
      setButtonState(LOADING_STATE);
      handleGetDownloadDocumentUrl(content, attachment)
        .then((getDownloadDocumentResponse) => {
          setButtonState(INITIAL_STATE);
          alertHandler.createNewAlertEntry({
            alerts,
            setAlerts,
            header: t("aic_doc_page_button_download_header_success"),
            fade: true,
            isSuccess: true,
            text: t("aic_doc_page_button_download_text_success"),
          });
          window.open(getDownloadDocumentResponse.s3PresignedUrl, "_blank");
        })
        .catch(() => {
          alertHandler.createNewAlertEntry({
            alerts,
            setAlerts,
            header: t("aic_doc_page_button_download_header_error"),
            fade: true,
            isSuccess: false,
            text: t("aic_doc_page_button_download_text_error"),
          });
          setButtonState(INITIAL_STATE);
        });
    },
    [content, t, setButtonState, alerts, setAlerts]
  );

  const actionComponent = useMemo(() => {
    const downloadDropdownOptions: {
      label: string;
      action?: string;
    }[] = [
      {
        label: t(DOWNLOAD_BUTTON_STRING_TEMPLATE),
        action: undefined,
      },
      ...(content.attachments ?? []).map((attachment: string) => ({
        label: t(
          `aic_doc_page_button_download_${content.countryCode.toLowerCase()}_${content.documentType.toLowerCase()}_${attachment.toLowerCase()}`,
          capitalizeText(attachment.replaceAll("_", " "))
        ),
        action: attachment.toLowerCase(),
      })),
    ];

    const onAction = (event: KatDropdownButton.ActionEvent) => {
      event.preventDefault();
      const {
        detail: { action },
      } = event;

      handleClick(action);
    };

    return shouldShowButton(content) &&
      (content.attachments ?? []).length > 0 ? (
      <KatDropdownButton
        id={`download-document-dropdown-${content.documentId}`}
        size="base"
        onAction={onAction}
        variant="primary"
        disabled={buttonState.loading}
        options={downloadDropdownOptions}
      />
    ) : (
      <KatButton
        id={`download-document-button-${content.documentId}`}
        label={t(DOWNLOAD_BUTTON_STRING_TEMPLATE)}
        type="button"
        variant={buttonState.variant}
        onClick={() => handleClick()}
        disabled={buttonState.loading}
        loading={buttonState.loading}
      />
    );
  }, [content, buttonState, t, handleClick]);

  return <>{actionComponent}</>;
};

export type DownloadDocumentButtonProps =
  DataTableCellChildComponentProps<InboundDocument>;

export default withPermissionsOrDefault(
  DownloadDocumentButton,
  Operation.DownloadInboundDocuments
);
